import React, { useMemo, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  IconButton,
} from '@mui/material';
import { ChevronRight, Close } from '@mui/icons-material';
import { debounce } from 'lodash';
import parse from 'html-react-parser';
import { useSearchContext } from 'views/App/Portal/Policy/SearchManuals/context';
import { warning, neutral } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import { $getNodeByKey, $getSelection, $isRangeSelection, LexicalEditor, $insertNodes, $createTextNode } from 'lexical';
import { PhxCrossReferenceLinkNode, $createPhxCrossReferenceLinkNode } from '../../nodes/PhxCrossReferenceLinkNode';
import { $createPhxParagraphNode } from '../../nodes/PhxParagraphNode';
import { useConfirm } from 'material-ui-confirm';
import { usePolicyEditorContext } from 'views/App/Portal/Policy/DraftManuals/Detail/PolicyEditor/context';
import { usePrefaceEditorContext } from 'views/App/Portal/Policy/DraftManuals/Detail/PrefaceEditor/context';
import { DraftManualDetailContext } from 'views/App/Portal/Policy/DraftManuals/Detail/context';

interface CrossReferenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectReference: (item: any) => void;
  activeEditor: LexicalEditor;
  editingNodeKey?: string | null;
}

const CrossReferenceDialog: React.FC<CrossReferenceDialogProps> = ({
  open,
  onClose,
  onSelectReference,
  activeEditor,
  editingNodeKey = null,
}) => {
  const {
    searchResults,
    searchManuals,
    loading,
    searchInputValue,
    setSearchInputValue,
    getHighlightedText,
    selectedItem,
    setSelectedItem,
    isSearchOpen,
    setIsSearchOpen,
  } = useSearchContext();
  const confirm = useConfirm();
  const { editorType } = useContext(DraftManualDetailContext);

  const policyCtx = editorType === 'policy' ? usePolicyEditorContext() : null;
  const prefaceCtx = editorType === 'preface' ? usePrefaceEditorContext() : null;

  const addCrossReferenceToSection = policyCtx?.addCrossReferenceToSection ?? (() => {});
  const removeCrossReferenceFromSection = policyCtx?.removeCrossReferenceFromSection ?? (() => {});
  const currentSection = policyCtx?.currentSection ?? null;
  const policyData = policyCtx?.policyData ?? null;

  useEffect(() => {
    setIsSearchOpen(true);
    return () => {
      setIsSearchOpen(false);
    };
  }, [setIsSearchOpen]);

  useEffect(() => {
    if (open) {
      setSearchInputValue('');
    }
  }, [open, setSearchInputValue]);

  useEffect(() => {
    if (open && editingNodeKey) {
      activeEditor.update(() => {
        const node = $getNodeByKey(editingNodeKey);
        if (node && node instanceof PhxCrossReferenceLinkNode) {
          setSelectedItem({
            contentUri: node.__contentUri,
            title: node.getTextContent(),
            id: node.__id,
          });
        }
      });
    }
  }, [open, editingNodeKey, activeEditor, setSelectedItem]);

  const getSectionTitle = () => {
    if (policyCtx && currentSection && policyData?.sections) {
      const mainSection = policyData.sections.find((section) => section.id === currentSection);
      if (mainSection) return mainSection.title;
      for (const section of policyData.sections) {
        const subSection = section.subSections?.find((sub) => sub.id === currentSection);
        if (subSection) return subSection.title;
      }
      return 'Untitled Section';
    }
    if (prefaceCtx) {
      return prefaceCtx.prefaceData?.title || 'Preface';
    }
    return 'Untitled Section';
  };

  const handleSearchDebounced = useMemo(
    () =>
      debounce((value: string) => {
        searchManuals(value);
      }, 500),
    [searchManuals]
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    searchManuals(value);
  };

  const handleSelectResult = (asset: any) => {
    setSelectedItem(asset);
  };

  const isInsertDisabled = () => !selectedItem;

  const handleConfirm = () => {
    if (!selectedItem) return;

    const linkTitle = selectedItem.title || '';
    const contentUri = selectedItem.contentUri;
    const crossReferenceId = uuidv4();
    if (!contentUri) {
      throw new Error('Content URI is required for cross reference');
    }

    addCrossReferenceToSection(currentSection, {
      id: crossReferenceId,
      contentUri,
    });

    activeEditor.update(() => {
      if (editingNodeKey) {
        const node = $getNodeByKey(editingNodeKey);
        if (node && node instanceof PhxCrossReferenceLinkNode) {
          const writableNode = node.getWritable();
          writableNode.__id = crossReferenceId;
          writableNode.__contentUri = contentUri;
          writableNode.setTextContent(linkTitle);
          return;
        }
      }

      // Create the cross reference node
      const newNode = $createPhxCrossReferenceLinkNode(crossReferenceId, contentUri, linkTitle);
      const selection = $getSelection();

      if (selection && $isRangeSelection(selection)) {
        // Insert at the current selection
        selection.insertNodes([newNode]);

        // Add a space after the node if needed
        const nextNode = selection.getNodes()[0];
        if (nextNode && nextNode.getTextContent() !== ' ') {
          selection.insertNodes([$createTextNode(' ')]);
        }
      } else {
        // If no selection, insert at the end of the current paragraph
        const root = activeEditor.getRootElement();
        if (root) {
          const paragraph = $createPhxParagraphNode();
          paragraph.append(newNode);
          paragraph.append($createTextNode(' '));
          $insertNodes([paragraph]);
        }
      }
    });

    setSearchInputValue('');
    searchManuals('');
    onSelectReference(selectedItem);
    onClose();
  };

  const handleRemove = () => {
    confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: 'Remove',
      description: `Do you want to remove this cross reference? ${selectedItem?.title || ''}`,
      title: 'Cross reference',
      cancellationText: 'Cancel',
    })
      .then(() => {
        if (editingNodeKey) {
          activeEditor.update(() => {
            const node = $getNodeByKey(editingNodeKey);
            if (node) {
              node.remove();
            }
          });
        }
        if (policyCtx && selectedItem) {
          removeCrossReferenceFromSection(currentSection, selectedItem.id);
        }
        onClose();
      })
      .catch(() => {
        return false;
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant='overline'
            display='block'
            sx={{ color: neutral[600] }}
          >
            {t('contentEditor.toolbar.cross_reference.title')}
          </Typography>
          <Typography variant='h6'>{getSectionTitle()}</Typography>
        </Box>
        <IconButton
          edge='end'
          color='inherit'
          onClick={onClose}
          aria-label='close'
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Typography
        variant='body2'
        sx={{ mb: 2, paddingX: 6, paddingTop: 2 }}
      >
        {t('contentEditor.toolbar.cross_reference.description')}
      </Typography>
      <DialogContent
        sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ flex: '0 0 auto' }}>
          <Box mb={2}>
            <TextField
              fullWidth
              variant='outlined'
              placeholder={t('draftManuals.searchManuals.inputPlaceholder')}
              value={searchInputValue}
              onChange={onChangeSearch}
              onMouseDown={(e) => e.stopPropagation()}
              InputProps={{
                endAdornment: loading ? <CircularProgress size={20} /> : null,
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
          {!loading && searchResults?.length ? (
            searchResults.map((option: any) => {
              if (!option?.contentUri) return null;
              const isSelected = selectedItem && selectedItem.contentUri === option.contentUri;
              return (
                <Box
                  key={option.contentUri}
                  sx={{
                    cursor: 'pointer',
                    mb: 2,
                    borderRadius: 1,
                    backgroundColor: isSelected ? '#E7F3FF' : 'transparent',
                    '&:hover': { backgroundColor: '#F3F8FE' },
                  }}
                  onClick={() => handleSelectResult(option)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid lightgrey',
                    }}
                  >
                    <Stack
                      spacing={1}
                      sx={{ p: 2, minWidth: 0, maxWidth: '100%' }}
                    >
                      <Typography
                        variant='body1'
                        color='primary'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {option.heading ? `${option.heading} - ${option.title}` : option.title}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {`(${option.manualName})`}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '& strong': {
                            backgroundColor: warning[100],
                            padding: '0 2px',
                            borderRadius: 1,
                          },
                        }}
                      >
                        {parse(getHighlightedText(option.highlights))}
                      </Typography>
                    </Stack>
                    <ChevronRight sx={{ color: isSelected ? '#007AFF' : 'inherit' }} />
                  </Box>
                </Box>
              );
            })
          ) : !loading && !searchResults?.length ? (
            <Box my={3}>
              <Typography color='textSecondary'>{t('contentEditor.toolbar.cross_reference.no_results')}</Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button onClick={onClose}>{t('contentEditor.toolbar.cross_reference.cancel')}</Button>
        {editingNodeKey && (
          <Button
            onClick={handleRemove}
            color='error'
          >
            Remove
          </Button>
        )}
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={isInsertDisabled()}
        >
          {editingNodeKey ? 'Update' : 'Insert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrossReferenceDialog;
